import React, { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useToast } from "@/components/ui/use-toast";
import { FormProvider, useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { LoadingButton } from "@/components/ui/common/loading-button";
import { Separator } from "@/components/ui/separator";

import { ErrorType } from "@/types";
import { InvoiceStatus, InvoiceType } from "@/types/enums";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { useUpdateSystemSettingsMutation } from "@/services/systemConfigApi";
import { SystemSettings } from "@/types/system-setting/system-config";

interface TimeSlot {
  opening: string;
  closing: string;
}

interface DayConfig {
  isSelected: boolean;
  times: TimeSlot;
}

type DaysState = {
  [key: string]: DayConfig;
};
interface BusinessHours {
  opening_time: string;
  closing_time: string;
}

interface GymTimingsFormProps {
  isOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  action: string;
  setAction: React.Dispatch<React.SetStateAction<"add" | "edit">>;
  refetch: () => void;
  editModeData: Record<string, string | BusinessHours> | undefined;
}
const GymTimingsForm = ({
  isOpen,
  setOpen,
  action,
  setAction,
  editModeData,
  refetch,
}: GymTimingsFormProps) => {
  const [updateSetting, { isLoading, error, status }] = useUpdateSystemSettingsMutation();

  const initialDaysState: DaysState = {
    S: {
      isSelected: false,
      times: { opening: "", closing: "" }
    },
    M: {
      isSelected: false,
      times: { opening: "", closing: "" }
    },
    T: {
      isSelected: false,
      times: { opening: "", closing: "" }
    },
    W: {
      isSelected: false,
      times: { opening: "", closing: "" }
    },
    TH: {
      isSelected: false,
      times: { opening: "", closing: "" }
    },
    F: {
      isSelected: false,
      times: { opening: "", closing: "" }
    },
    SA: {
      isSelected: false,
      times: { opening: "", closing: "" }
    },
  }
  const [days, setDays] = useState<DaysState>(initialDaysState);

  const toggleDay = (day: string) => {
    setDays((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        isSelected: !prev[day].isSelected,
      },
    }));
  };

  const updateTime = (
    day: string,
    field: "opening" | "closing",
    value: string
  ) => {
    setDays((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        times: {
          ...prev[day].times,
          [field]: value,
        },
      },
    }));
  };

  const dayNames = {
    S: "Sunday",
    M: "Monday",
    T: "Tuesday",
    W: "Wednesday",
    TH: "Thursday",
    F: "Friday",
    SA: "Saturday",
  };

  const { toast } = useToast();
  const initialValues = {
    group_name: "gym_timings",
  };

  const form = useForm<SystemSettings>({
    mode: "all",
    defaultValues: initialValues,
  });

  const {
    control,
    watch,
    register,
    handleSubmit,
    clearErrors,
    getValues,
    reset,
    setValue,
    formState: { isSubmitting, errors },
  } = form;
  const watcher = watch();

  useEffect(() => {
    if (action === "edit" && editModeData) {
      const payload = { ...editModeData, group_name: "gym_timings" };
      reset(payload as SystemSettings);

      // Map day names from API to form keys
      const dayMapping: { [key: string]: string } = {
        monday: "M",
        tuesday: "T",
        wednesday: "W",
        thursday: "TH",
        friday: "F",
        saturday: "SA",
        sunday: "S"
      };

      // Create a new days state based on editModeData
      const updatedDays = { ...initialDaysState };

      // Process each day in the editModeData
      Object.entries(editModeData).forEach(([key, value]) => {
        // Skip non-day fields
        if (dayMapping[key]) {
          try {
            // Check if value is already an object or a JSON string
            let hours: { opening_time: string; closing_time: string };
            if (typeof value === 'string') {
              // Convert Python-style dictionary string to JSON format
              const jsonString = value
                .replace(/'/g, '"')  // Replace single quotes with double quotes
                .replace(/True/g, 'true')  // Replace Python boolean
                .replace(/False/g, 'false'); // Replace Python boolean
              hours = JSON.parse(jsonString) as { opening_time: string; closing_time: string };
            } else {
              hours = value as { opening_time: string; closing_time: string };
            }

            // Update the days state for this day
            const dayKey = dayMapping[key];
            updatedDays[dayKey] = {
              isSelected: true,
              times: {
                opening: hours.opening_time,
                closing: hours.closing_time
              }
            };
          } catch (e) {
            console.warn(`Failed to parse hours for ${key}:`, e);
          }
        }
      });

      // Update the days state
      setDays(updatedDays);
    } else {
      reset(initialValues);
      setDays(initialDaysState);
    }
  }, [action, editModeData]);

  const validateForm = () => {
    const selectedDays = Object.entries(days).filter(([_, value]) => value.isSelected);

    if (selectedDays.length === 0) {
      toast({
        variant: "destructive",

        title: "Please select at least one day"
      });
      return false;
    }

    for (const [day, value] of selectedDays) {
      if (!value.times.opening || !value.times.closing) {
        toast({
          variant: "destructive",

          title: `Please fill both opening and closing times for ${dayNames[day as keyof typeof dayNames]}`
        });
        return false;
      }

      if (value.times.opening === value.times.closing) {
        toast({
          variant: "destructive",

          title: `Opening and closing times cannot be the same for ${dayNames[day as keyof typeof dayNames]}`
        });
        return false;
      }
    }

    return true;
  };

  const onSubmit = async (formData: SystemSettings) => {
    try {
      if (!validateForm()) {
        return false;
      }

      const timingPayload: any = {};

      // Map days to their full names and add selected times
      const dayMapping: { [key: string]: string } = {
        M: "monday",
        T: "tuesday",
        W: "wednesday",
        TH: "thursday",
        F: "friday",
        SA: "saturday",
        S: "sunday"
      };

      Object.entries(days).forEach(([key, value]) => {
        if (value.isSelected && value.times.opening && value.times.closing) {
          const dayName = dayMapping[key];
          timingPayload[dayName] = JSON.stringify({
            opening_time: value.times.opening,
            closing_time: value.times.closing
          });
        }
      });

      const resp = await updateSetting({ gym_timings: timingPayload }).unwrap();
      if (resp) {
        toast({
          variant: "success",
          title: "Gym timing updated successfully",
        });
        refetch();
        handleClose();
        return resp;
      }
    } catch (error) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in Submission",
          description: `${typedError.data?.detail || typedError.data?.message}`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in Submission",
          description: `Something Went Wrong.`,
        });
      }
      return false;
    }
  };

  const onError = () => {
    toast({
      variant: "destructive",
      title: "Please fill all the mandatory fields",
    });
  };

  const handleClose = () => {
    clearErrors();
    setAction("add");
    setDays(initialDaysState); // Reset the days state to initial values
    reset(
      initialValues,
      {
        keepIsSubmitted: false, // Reset the form's submission state
        keepSubmitCount: false, // Reset the count of submissions
        keepDirtyValues: false, // Clear any dirty values (set to false to reset all fields)
        keepDefaultValues: true, // Keep default values as provided by initialValue
      }
    );

    setOpen(false);
  };

  console.log({ watcher, errors, action });
  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent
        hideCloseButton
        className="bg-formbg w-full lg:max-w-2xl p-0 custom-scrollbar h-full "
      >
        <FormProvider {...form}>
          <form
            key={action}
            noValidate
            className="pb-4 "
            onSubmit={handleSubmit(onSubmit, onError)}
          >
            <SheetHeader className="px-6 sticky top-0 z-40 py-3 bg-white">
              <SheetTitle>
                <div className="flex justify-between flex-col md:flex-row gap-5 items-start bg-white">
                  <div>
                    <p className="font-semibold">System Configuration</p>
                    <div className="text-sm">
                      <span className="text-gray-400 pr-1 font-semibold">
                        System Configuration
                      </span>{" "}
                      <span className="text-gray-400 font-semibold">/</span>
                      <span className="pl-1 text-primary font-semibold ">
                        {action == "add" ? "Create" : "Edit"} Gym Timings
                      </span>
                    </div>
                  </div>

                  <div className="flex justify-center space-x-[20px]">
                    <Button
                      type="button"
                      className="w-[100px] text-center flex items-center gap-2 border-primary"
                      variant={"outline"}
                      disabled={isSubmitting}
                      onClick={handleClose}
                    >
                      <i className="fa fa-xmark "></i>
                      Cancel
                    </Button>
                    <LoadingButton
                      type="submit"
                      loading={isSubmitting}
                      disabled={isSubmitting}
                      className="w-[110px] cursor-pointer bg-primary hover:bg-primary flex items-center gap-1"
                    >
                      {!isSubmitting &&
                        <i className="fa-regular fa-floppy-disk text-base px-1"></i>
                      }
                      {action == "add" ? "Save" : "Update"}
                    </LoadingButton>
                  </div>
                </div>
              </SheetTitle>
            </SheetHeader>
            <Separator className=" h-[1px] rounded-full     " />

            <SheetDescription className=" h-full  p-6  ">
              <Card className="w-full">
                <CardHeader>
                  <CardTitle className="text-base font-medium">
                    Gym Timing
                  </CardTitle>
                  <Separator className="h-[1px]" />
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Days of the Week */}
                  <div className="space-y-2">
                    <div className="text-sm text-zinc-500">
                      Days of the Week:
                    </div>
                    <div className="flex gap-2">
                      {Object.entries(days).map(([key, value]) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => toggleDay(key)}
                          className={cn(
                            "h-8 w-8 rounded-md text-sm font-medium transition-colors",
                            value.isSelected
                              ? "bg-primary text-white"
                              : "bg-white text-zinc-600 hover:bg-zinc-100"
                          )}
                        >
                          {key.charAt(0)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Time Slots */}
                  <div className="space-y-4">
                    {Object.entries(days).map(
                      ([key, value]) =>
                        value.isSelected && (
                          <div
                            key={key}
                            className="flex gap-4 justify-start items-center "
                          >
                            <div className="flex items-center w-full max-w-[80px] ">
                              <p className="text-sm text-zinc-600 text-right w-full">
                                {dayNames[key as keyof typeof dayNames]}
                              </p>
                            </div>

                            {/* Opening Time */}
                            <div className="space-y-2">
                              <div className="text-sm text-zinc-500">
                                Opening hours
                              </div>
                              <div className="flex gap-2">
                                <Input
                                  placeholder="HH:MM"
                                  value={value.times.opening}
                                  onChange={(e) =>
                                    updateTime(key, "opening", e.target.value)
                                  }
                                  type="time"
                                  className={`border-checkboxborder focus-within:ring-0 w-full max-w-[200px] h-8`}
                                />
                              </div>
                            </div>

                            {/* Closing Time */}
                            <div className="space-y-2">
                              <div className="text-sm text-zinc-500">
                                Closing Time
                              </div>
                              <div className="flex gap-2">
                                <Input
                                  placeholder="HH:MM"
                                  value={value.times.closing}
                                  onChange={(e) =>
                                    updateTime(key, "closing", e.target.value)
                                  }
                                  type="time"
                                  className={`border-checkboxborder focus-within:ring-0 w-full max-w-[200px] h-8`}
                                />
                              </div>
                            </div>
                          </div>
                        )
                    )}
                  </div>
                </CardContent>
              </Card>
            </SheetDescription>
          </form>
        </FormProvider>
      </SheetContent>
    </Sheet>
  );
};

export default GymTimingsForm;
